import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { InboxService } from '../../../../../../../../services/inbox/inbox.service';
import { StaffApprovalAction } from '../../../../../../../../models/inbox';
import { CallbackResult, CallbackResultStatus, dateStringFromISO8601, timeStringFromISO8601 } from '../../../../../../../../utils';
import { AccountService } from '../../../../../../../../services/account/account.service';
import { Association, AssociationMember } from '../../../../../../../../models/association';
import { AssociationService } from '../../../../../../../../services/association/association.service';

@Component({
  selector: 'app-review-association-join-request-modal',
  templateUrl: './review-association-join-request-modal.component.html',
  styleUrls: ['./review-association-join-request-modal.component.css']
})
export class ReviewAssociationJoinRequestModalComponent implements OnInit {

  @Input() associationId: number;
  @Input() associationMemberId: number;
  @Input() showHideOption: boolean;
  @Input() savedCallback: (result: CallbackResult) => undefined;

  public association: Association;
  public associationMember: AssociationMember;

  public selectedAction: StaffApprovalAction = null;

  public loadingAssociation = true;
  public loadingAssociationMember = true;
  public loadingSubmit = false;
  public notFound = false;
  public errorMessage = '';
  public serverError: number = null;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public StaffApprovalAction = StaffApprovalAction;
  public dateStringFromISO8601 = dateStringFromISO8601;
  public timeStringFromISO8601 = timeStringFromISO8601;

  constructor(
    public activeModal: NgbActiveModal,
    private accountService: AccountService,
    private associationService: AssociationService,
    private inboxService: InboxService
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }
    this.loadAssociation();
    this.loadAssociationMember();
  }

  /**
   * Whether a valid input has been selected. Used to determine whether the
   * save button is clickable.
   */
  public validInputSelected(): boolean {
    return (
      [
        StaffApprovalAction.approve,
        StaffApprovalAction.deny,
        StaffApprovalAction.hide
      ].indexOf(this.selectedAction) >= 0
    );
  }

  public handleSave() {
    this.loadingSubmit = true;

    this.errorMessage = '';

    if (!this.validInputSelected()) {
      this.errorMessage = 'Please choose a valid option.';
      this.loadingSubmit = false;
      return;
    }

    let savePromise: Promise<void>;
    let resultText: string;

    switch (this.selectedAction) {
      case StaffApprovalAction.approve:
        savePromise = this.associationService
          .approveAssociationJoinRequest(this.associationMemberId);
        resultText = 'Request to join approved.';
        break;
      case StaffApprovalAction.deny:
        savePromise = this.associationService
          .removeAssociationMember(this.associationMember.url);
        resultText = 'Request to join rejected.';
        break;
      case StaffApprovalAction.hide:
        savePromise = this.associationService
          .hideAssociationJoinRequest(this.associationMemberId);
        resultText = 'Request to join hidden.';
        break;
    }

    savePromise
      .then(() => {
        this.inboxService.loadInboxCounts(true).then();
        this.savedCallback({
          text: resultText,
          status: CallbackResultStatus.success
        });
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to review this association join request.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally();
  }

  private loadAssociation() {
    this.loadingAssociation = true;

    this.associationService
      .getAssociation(this.associationId)
      .then(association => {
        this.association = association;
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingAssociation = false; });
  }

  private loadAssociationMember() {
    this.loadingAssociationMember = true;

    this.associationService
      .getAssociationMember(this.associationMemberId)
      .then(associationMember => {
        this.associationMember = associationMember;
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingAssociationMember = false; });
  }

}
